import { type FC, useRef, useEffect, useReducer } from 'react';

import { GetHelpZendeskEmbedded } from './GetHelpZendeskEmbedded.tsx';
import { GetHelpZendeskModal } from './GetHelpZendeskModal.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { STEPS } from './GetHelpZendeskConstants.ts';
import { type Props } from './GetHelpZendeskTypes.ts';
import { getHelpZendeskReducer, initialState } from './GetHelpZendeskReducer.ts';
import {
    trackGetHelpZendeskEvent,
    trackGetHelpZendeskEmbeddedEvent,
} from './getHelpZendeskTracking.ts';
import {
    getGetHelpZendeskLocalStorageState,
    resetGetHelpZendeskLocalStorageState,
} from './getHelpZendeskHelpers.ts';
import { trackGetHelpZendeskVariant, trackGetHelpSellerZendeskVariant } from '../helpers.tsx';

const topicToArticleMap: { [key: string]: string } = {
    taxExempt: '14491937576091',
    auctions: '14956947715611',
    orderCancellation: '14544967384731',
    trade1stProgram: '14488817505947',
    accountSettings: '14520526026139',
};

const GetHelpZendesk: FC<Props> = ({ isEmbedded, placement, isSeller, onClose, ...props }) => {
    const presetGetHelpZendeskOptions = getGetHelpZendeskLocalStorageState();

    const presetProps =
        props.isCheckout && presetGetHelpZendeskOptions.step === 'orderInfo'
            ? null
            : presetGetHelpZendeskOptions;

    const isOpenedByCtaRef = useRef(!presetGetHelpZendeskOptions?.isOpen);
    const isOpenedByCta = isOpenedByCtaRef?.current;

    const [state, dispatch] = useReducer(getHelpZendeskReducer, {
        ...initialState,
        ...props,
        isEmbedded,
        isSeller,
        ...(props.topic &&
            topicToArticleMap?.[props.topic] && {
                articleId: topicToArticleMap[props.topic],
                stepsPath: [
                    { step: STEPS.INTRO },
                    { step: STEPS.ARTICLE, articleId: topicToArticleMap[props.topic] },
                ],
                step: STEPS.ARTICLE,
            }),
        ...(props.orderId &&
            props.showOrderInfo && {
                step: isSeller ? STEPS.SELLER_ORDER : STEPS.ORDER_INFO,
                stepsPath: isSeller
                    ? [{ step: STEPS.INTRO }, { step: STEPS.SELLER_ORDER }]
                    : [{ step: STEPS.INTRO }, { step: STEPS.ORDER_INFO }],
            }),
        ...(isSeller &&
            props.itemId &&
            props.showItemInfo && {
                step: STEPS.SELLER_ITEM,
                stepsPath: [{ step: STEPS.INTRO }, { step: STEPS.SELLER_ITEM }],
            }),
        onClose: options => {
            if (!options?.isClosedAutomatically) {
                trackGetHelpZendeskEvent({
                    stepInteractionName: 'Get Help Modal Closed',
                    isSeller: !!isSeller,
                });
            }

            resetGetHelpZendeskLocalStorageState();
            onClose();
        },
        ...(presetProps && presetProps?.isOpen && { ...presetProps }),
    });

    useEffect(() => {
        if (!isEmbedded && isOpenedByCta) {
            if (isSeller) {
                trackGetHelpSellerZendeskVariant();
            } else {
                trackGetHelpZendeskVariant();
            }

            trackGetHelpZendeskEvent({
                stepInteractionName: 'Get Help Modal Opened',
                trigger: placement || '',
                isSeller: !!isSeller,
            });
        }

        if (isEmbedded) {
            trackGetHelpZendeskVariant();
            trackGetHelpZendeskEmbeddedEvent({ stepInteractionName: 'contact us page opened' });
        }
    }, [isOpenedByCta, isEmbedded, placement, isSeller]);

    return (
        <GetHelpZendeskContext value={{ dispatch, state }}>
            <div>{isEmbedded ? <GetHelpZendeskEmbedded /> : <GetHelpZendeskModal />}</div>
        </GetHelpZendeskContext>
    );
};
export { GetHelpZendesk };
